// The rate card's arithmetic and grouping, apart from the screen that draws it.
//
// The API returns one row per PERIOD; a rate card is one row per KEY. Those are
// different lists, and the difference is the whole reason this screen is not a
// plain table of the response: a price that superseded another is the same key,
// and showing them as two rows says the provider charges two prices at once.

import type { FactSource, ProviderRate, RateUnit, UsageRateGap } from "@crm/shared";
import { formatInstantDate } from "../../format/calendarDate.js";
import { factSourceLabel } from "../../format/labels.js";
import { formatCount } from "../../format/money.js";

/** The five fields that identify a price. A rate is a price for one of these. */
export interface RateKey {
  provider: string;
  operation: string;
  model: string | null;
  region: string | null;
  unit: RateUnit;
}

export interface RateGroup {
  /** Stable across renders: the key's own fields, joined. */
  id: string;
  key: RateKey;
  /**
   * The period covering now, or null. Null is a real state: a rate whose
   * period ended prices nothing at all, so a key with only closed periods is
   * unpriced today even though it has a history.
   */
  standing: ProviderRate | null;
  /** Every other period, newest first. */
  history: ProviderRate[];
}

function keyOf(rate: ProviderRate): RateKey {
  return {
    provider: rate.provider,
    operation: rate.operation,
    model: rate.model,
    region: rate.region,
    unit: rate.unit,
  };
}

function keyId(key: RateKey): string {
  return [key.provider, key.operation, key.model ?? "", key.region ?? "", key.unit].join("|");
}

/** What a key is called on screen and in a table's accessible name. */
export function keyLabel(key: RateKey): string {
  return `${key.provider} ${key.operation} ${key.model ?? "any model"}`;
}

/**
 * The period in force at `now`. Half-open, [effective_from, effective_to): the
 * day a period ends is the first day of the next one, so a rate is never
 * counted twice and never leaves a gap of one day.
 */
function coversNow(rate: ProviderRate, now: string): boolean {
  return rate.effective_from <= now && (rate.effective_to === null || rate.effective_to > now);
}

/** Newest period first — the order a history reads in. */
function byEffectiveFromDesc(a: ProviderRate, b: ProviderRate): number {
  return a.effective_from < b.effective_from ? 1 : a.effective_from > b.effective_from ? -1 : 0;
}

export function groupRates(rates: readonly ProviderRate[], now: string): RateGroup[] {
  const groups = new Map<string, RateGroup>();

  for (const rate of rates) {
    const key = keyOf(rate);
    const id = keyId(key);
    const existing = groups.get(id);
    if (existing === undefined) {
      groups.set(id, { id, key, standing: null, history: [] });
    }
    const group = groups.get(id);
    if (group === undefined) continue;
    if (group.standing === null && coversNow(rate, now)) {
      group.standing = rate;
    } else {
      group.history.push(rate);
    }
  }

  for (const group of groups.values()) group.history.sort(byEffectiveFromDesc);

  return [...groups.values()].sort((a, b) => keyLabel(a.key).localeCompare(keyLabel(b.key)));
}

/** Free-text match over the three fields a person searches a rate card by. */
export function matchesSearch(group: RateGroup, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === "") return true;
  return [group.key.provider, group.key.operation, group.key.model ?? "", group.key.unit].some(
    (field) => field.toLowerCase().includes(needle),
  );
}

/** A token unit is priced per million by every provider's own price list. */
export function isTokenUnit(unit: RateUnit): boolean {
  return unit.endsWith("token");
}

/**
 * A per-token price as minor units per MILLION tokens.
 *
 * Null when the wire string is not a number — `price_per_unit` is a decimal
 * string precisely because a price of 0.0000008 has no exact double, so this
 * derived figure is a convenience beside the exact price and never instead of
 * it. Zero is returned as zero only for a price that IS zero; a price too small
 * to round to a cent per million is reported by the caller as "under $0.01"
 * rather than as nothing.
 */
export function perMillionMinor(pricePerUnit: string): number | null {
  const price = Number(pricePerUnit);
  if (!Number.isFinite(price)) return null;
  return Math.round(price * 1_000_000 * 100);
}

/**
 * The command that prices a gap.
 *
 * Written out in full rather than offered as a button, because this screen has
 * no write path yet and a person acting from it acts in a shell. `unit`,
 * `price_per_unit` and `currency` are the four answers only a person has;
 * `effective_from` defaults to the day the gap opened, so the price reaches the
 * facts that revealed it rather than only the ones that come next.
 */
export function setRateCommand(gap: UsageRateGap): string {
  const body = {
    provider: gap.provider,
    operation: gap.operation,
    model: gap.model,
    unit: "<unit>",
    price_per_unit: "<price>",
    currency: "<USD|CAD>",
    effective_from: `${gap.first_seen_at.slice(0, 10)}T00:00:00Z`,
  };
  return `crm provider-rates set-provider-rate --body '${JSON.stringify(body)}'`;
}

/** A gap somebody can still act on: unresolved, in a month that is still open. */
export function isOpenGap(gap: UsageRateGap): boolean {
  return gap.resolved_at === null && gap.period_open;
}

// ---------------------------------------------------------------------------
// Who said so.
//
// The nightly feeds write rate rows beside the ones a person types. The two are
// not the same claim: a feed reports a provider's own list price, and a person
// reports a price somebody negotiated and is answerable for. A reader who
// cannot tell them apart cannot tell which figure to trust, so every row says
// who wrote it and when.
// ---------------------------------------------------------------------------

/**
 * The feed name for each `external_ref` prefix we know.
 *
 * A closed map with a fallback: the prefix is the feed's own id, so an id that
 * is not here is still printed as it is. An unknown feed is a feed we have not
 * named yet, never an unknown row.
 */
const FEED_NAME_BY_PREFIX: Readonly<Record<string, string>> = {
  "azure-retail-prices": "Azure Retail Prices",
  "aws-bedrock-price-list": "AWS Price List",
  "twilio-voice-pricing": "Twilio Pricing",
};

/** The feed id an `external_ref` carries: the part before the first colon. */
function refPrefix(externalRef: string): string | null {
  const colon = externalRef.indexOf(":");
  if (colon <= 0) return null;
  return externalRef.slice(0, colon);
}

/**
 * What a feed is called on screen.
 *
 * The ref's prefix names the feed. With no ref, or with a ref that carries no
 * prefix, the source enum is the best name there is — `twilio_api` is still
 * "Twilio", which is more than "a feed" says.
 */
export function feedName(source: FactSource, externalRef: string | null): string {
  const prefix = externalRef === null ? null : refPrefix(externalRef);
  if (prefix === null) return factSourceLabel(source);
  return FEED_NAME_BY_PREFIX[prefix] ?? prefix;
}

/**
 * Who wrote a rate, ready to draw.
 *
 * Two kinds, because the screen makes exactly one distinction: a person, or a
 * machine. Every source that is not `person` is a feed of some kind, and each
 * one names itself through `feedName`.
 */
export type RateAttribution =
  | { readonly kind: "person"; readonly label: string; readonly receivedAt: string }
  | {
      readonly kind: "feed";
      readonly label: string;
      readonly externalRef: string | null;
      readonly receivedAt: string;
    };

export function rateAttribution(rate: ProviderRate): RateAttribution {
  if (rate.source === "person") {
    return { kind: "person", label: factSourceLabel(rate.source), receivedAt: rate.received_at };
  }
  return {
    kind: "feed",
    label: feedName(rate.source, rate.external_ref),
    externalRef: rate.external_ref,
    receivedAt: rate.received_at,
  };
}

/** The three chips above the card. */
export type RateSourceFilter = "all" | "person" | "feed";

/**
 * Does the key's standing price come from the chosen side?
 *
 * Read from the STANDING rate, because that is the price the row states. A key
 * with no price in force states no price, so it answers only to "all" — its own
 * row already says "No price in force".
 */
export function matchesSource(group: RateGroup, filter: RateSourceFilter): boolean {
  if (filter === "all") return true;
  if (group.standing === null) return false;
  return rateAttribution(group.standing).kind === filter;
}

export interface RateCardCounts {
  all: number;
  person: number;
  feed: number;
}

export function countBySource(groups: readonly RateGroup[]): RateCardCounts {
  return {
    all: groups.length,
    person: groups.filter((group) => matchesSource(group, "person")).length,
    feed: groups.filter((group) => matchesSource(group, "feed")).length,
  };
}

export interface RateCardSummary {
  /** Keys with a price in force today. `person` + `feed` add up to this. */
  inForce: number;
  fromFeeds: number;
  fromPeople: number;
  /**
   * The `received_at` of the newest rate a feed wrote, over every period the
   * page holds — not only the standing ones. A feed write that closed a period
   * is still the last time a feed spoke.
   */
  lastFeedWrite: string | null;
}

export function summariseRates(groups: readonly RateGroup[]): RateCardSummary {
  const counts = countBySource(groups);
  let lastFeedWrite: string | null = null;

  for (const group of groups) {
    const periods = group.standing === null ? group.history : [group.standing, ...group.history];
    for (const rate of periods) {
      if (rate.source === "person") continue;
      if (lastFeedWrite === null || rate.received_at > lastFeedWrite) {
        lastFeedWrite = rate.received_at;
      }
    }
  }

  return {
    inForce: counts.person + counts.feed,
    fromFeeds: counts.feed,
    fromPeople: counts.person,
    lastFeedWrite,
  };
}

/**
 * The card in one line, for the page header.
 *
 * The last clause is dropped when no feed has written a rate: a date nobody can
 * state is left unsaid, never printed as a dash or as today.
 */
export function rateCardSummaryLine(summary: RateCardSummary): string {
  const noun = summary.inForce === 1 ? "rate" : "rates";
  const head = `${formatCount(summary.inForce)} ${noun} in force`;
  const split = `${formatCount(summary.fromFeeds)} from feeds, ${formatCount(summary.fromPeople)} entered by a person`;
  if (summary.lastFeedWrite === null) return `${head} · ${split}`;
  return `${head} · ${split} · last feed write ${formatInstantDate(summary.lastFeedWrite)}`;
}
